import { useEffect, useRef, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  Drawer,
  LoadingSkeleton,
  Row,
} from "@narmi/design_system";
import { useLocation, useNavigate } from "react-router-dom";
import { useSetChatVisibility } from "cerulean";
import {
  formatNachaPassthroughSubmissionDetails,
  useFetchNachaPassthroughSubmission,
} from "byzantine/src/dbbl/businessLogic/entities/nachaPassthrough";
import {
  DisplayStatus,
  isPassthroughCancelable,
} from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity";
import type { FormattedFileImportItem } from "byzantine/src/dbbl/businessLogic/entities/fileImportActivity/types";
import NachaPassthroughTopCard from "../../DualApproval/Pending/TopCards/NachaPassthroughTopCard";
import { DrawerHeader } from "../common";
import { getNachaPassthroughPaymentsRoute } from "../routes";

type PassthroughImportDrawerProps = {
  isOpen: boolean;
  item: FormattedFileImportItem | null;
  itemUuid: API.NachaPassthrough.SubmissionId;
  handleClose: () => void;
  handleOnNext: () => void;
  handleOnPrev: () => void;
  handleToggleCancelDialog: (id: API.NachaPassthrough.SubmissionId) => void;
};

const PassthroughImportDrawer = ({
  isOpen,
  item,
  itemUuid,
  handleClose,
  handleOnNext,
  handleOnPrev,
  handleToggleCancelDialog,
}: PassthroughImportDrawerProps) => {
  const drawerIdRef = useRef(
    `Drawer-${Math.random().toString(36).slice(2, 11)}`
  );
  useSetChatVisibility(drawerIdRef.current, !isOpen);
  const { l10n } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const [submission, setSubmission] =
    useState<API.NachaPassthrough.Submission | null>(null);
  const {
    send: fetchSubmission,
    loading,
    error,
  } = useFetchNachaPassthroughSubmission();

  useEffect(() => {
    let isActive = true;
    setSubmission(null);
    fetchSubmission(itemUuid).then((response) => {
      if (isActive && response) setSubmission(response);
    });

    return () => {
      isActive = false;
    };
  }, [fetchSubmission, itemUuid]);

  // a deep link has no table row yet, so fall back to the fetched submission
  const displayStatus =
    item?.displayStatus ??
    (submission
      ? DisplayStatus[submission.state] || DisplayStatus.unknown
      : "");

  const renderFooter = () => {
    if (!isPassthroughCancelable(displayStatus)) return undefined;
    return (
      <Row justifyContent="end">
        <Row.Item shrink>
          <Button
            kind="primary"
            onClick={() => handleToggleCancelDialog(itemUuid)}
            label={l10n.getString("label-cancel-payment")}
            testId="cancel-passthrough-button"
          />
        </Row.Item>
      </Row>
    );
  };

  const handleClickViewPayments = () => {
    const returnTo = `${location.pathname}${location.search}`;
    navigate(
      `${getNachaPassthroughPaymentsRoute(
        itemUuid
      )}?returnTo=${encodeURIComponent(returnTo)}`
    );
  };

  return (
    <Drawer
      depth="524px"
      isOpen={isOpen}
      onUserDismiss={handleClose}
      paddingSize="xl"
      onNext={handleOnNext}
      onPrev={handleOnPrev}
      footer={renderFooter()}
    >
      <LoadingSkeleton isLoading={loading && !submission}>
        <DrawerHeader
          title={item?.title ?? submission?.title ?? ""}
          status={displayStatus}
        />
        {error ? (
          <div className="margin--top--l">
            <Alert kind="error" isActive isDismissable={false}>
              {l10n.getString("error-unknown")}
            </Alert>
          </div>
        ) : null}
        {submission ? (
          <NachaPassthroughTopCard
            details={formatNachaPassthroughSubmissionDetails(submission)}
            submissionId={submission.id}
            handleClickViewPayments={handleClickViewPayments}
          />
        ) : null}
      </LoadingSkeleton>
    </Drawer>
  );
};

export default PassthroughImportDrawer;
